/**
 * Campo data con il calendario di shadcn in un popover, al posto del
 * selettore del browser, che ogni sistema disegna a modo suo.
 *
 * Il valore e una data di calendario come "2026-09-23". Il calendario lavora
 * in UTC, come `formatoGiorno`: il giorno scelto e quello scritto, qualunque
 * sia il fuso del dispositivo; oggi e quello italiano. La settimana parte
 * dal lunedi, in italiano. Su un intervallo piu lungo di un anno, o senza
 * limiti, mese e anno si scelgono da due tendine: una data di nascita non si
 * raggiunge sfogliando. Un campo facoltativo, come un filtro, si svuota dal
 * calendario.
 */

import { useState } from 'react';
import { CalendarIcon, XIcon } from 'lucide-react';
import { it } from 'react-day-picker/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatoGiorno, giornoUtc, oggi } from './date.js';

const formatoCampo = formatoGiorno({
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** "2026-09-23" dalla mezzanotte UTC che il calendario restituisce. */
const perValore = (giorno: Date): string => giorno.toISOString().slice(0, 10);

/** I limiti delle tendine quando il campo non ne ha: dal 1940 a cinque anni da oggi. */
const PRIMO_ANNO = '1940-01-01';
const traCinqueAnni = () => `${Number(oggi().slice(0, 4)) + 5}-12-31`;

/** Piu di dodici mesi tra due date "2026-09-23": si sceglie dalle tendine. */
const oltreUnAnno = (da: string, a: string): boolean => {
  const mesi = (d: string) => Number(d.slice(0, 4)) * 12 + Number(d.slice(5, 7));
  return mesi(a) - mesi(da) > 12;
};

export function CampoData({
  id,
  valore,
  onCambia,
  min,
  max,
  invalido = false,
  cancellabile = false,
  segnaposto = 'Scegli il giorno',
  etichetta,
}: {
  id: string;
  /** "2026-09-23", o vuoto. */
  valore: string;
  onCambia: (valore: string) => void;
  /** Primo e ultimo giorno sceglibili, come "2026-07-01". */
  min?: string;
  max?: string;
  invalido?: boolean;
  /** Facoltativo: il calendario ha il pulsante per togliere la data. */
  cancellabile?: boolean;
  segnaposto?: string;
  /**
   * Nei filtri, al posto dell'etichetta sopra: e il segnaposto e, scelta la
   * data, le sta davanti («Dal 3 set 2026»).
   */
  etichetta?: string;
}) {
  const [aperto, setAperto] = useState(false);
  const scelto = valore ? giornoUtc(valore) : undefined;
  const primo = min ? giornoUtc(min) : undefined;
  const ultimo = max ? giornoUtc(max) : undefined;
  const tendine = oltreUnAnno(min ?? PRIMO_ANNO, max ?? traCinqueAnni());

  return (
    <Popover open={aperto} onOpenChange={setAperto}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          aria-invalid={invalido}
          aria-label={etichetta}
          className={cn(
            'w-full justify-start font-normal first-letter:uppercase',
            !scelto && 'text-muted-foreground',
          )}
        >
          <CalendarIcon data-icon="inline-start" />
          {scelto
            ? `${etichetta ? `${etichetta} ` : ''}${formatoCampo.format(scelto)}`
            : (etichetta ?? segnaposto)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          timeZone="UTC"
          locale={it}
          selected={scelto}
          // Oggi e il giorno italiano: a mezzanotte e mezza in UTC e ancora ieri.
          today={giornoUtc(oggi())}
          defaultMonth={scelto ?? primo}
          captionLayout={tendine ? 'dropdown' : 'label'}
          startMonth={primo ?? (tendine ? giornoUtc(PRIMO_ANNO) : undefined)}
          endMonth={ultimo ?? (tendine ? giornoUtc(traCinqueAnni()) : undefined)}
          disabled={[...(primo ? [{ before: primo }] : []), ...(ultimo ? [{ after: ultimo }] : [])]}
          onSelect={(giorno) => {
            if (!giorno) return;
            onCambia(perValore(giorno));
            setAperto(false);
          }}
        />
        {cancellabile && scelto && (
          <div className="border-t border-border p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                onCambia('');
                setAperto(false);
              }}
            >
              <XIcon data-icon="inline-start" />
              Togli la data
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
